export const STATUS_TONE = {
  DRAFT: "gray",
  PENDING_APPROVAL: "amber",
  SCHEDULED: "blue",
  OPENING_AUCTION: "blue",
  OPEN: "green",
  SUSPENDED: "amber",
  CLOSED: "gray",
  AWAITING_RESULT: "amber",
  RESULT_PROPOSED: "amber",
  RESULT_CONFIRMED: "blue",
  SETTLING: "blue",
  SETTLED: "green",
  CANCELLED: "red",
};

export const STATUS_LABEL = {
  DRAFT: "Draft",
  PENDING_APPROVAL: "Pending approval",
  SCHEDULED: "Approved, scheduled",
  OPENING_AUCTION: "Opening auction",
  OPEN: "Open",
  SUSPENDED: "Suspended",
  CLOSED: "Closed",
  AWAITING_RESULT: "Awaiting result",
  RESULT_PROPOSED: "Result proposed",
  RESULT_CONFIRMED: "Result confirmed",
  SETTLING: "Settling",
  SETTLED: "Settled",
  CANCELLED: "Cancelled",
};

export const STATUS_TABS = [
  { value: "", label: "All" },
  { value: "DRAFT", label: "Draft" },
  { value: "PENDING_APPROVAL", label: "Pending approval" },
  { value: "SCHEDULED", label: "Approved" },
  { value: "OPENING_AUCTION", label: "Opening auction" },
  { value: "OPEN", label: "Open" },
  { value: "SUSPENDED", label: "Suspended" },
  { value: "CLOSED", label: "Closed" },
  { value: "AWAITING_RESULT", label: "Awaiting result" },
  { value: "RESULT_PROPOSED", label: "Result proposed" },
  { value: "RESULT_CONFIRMED", label: "Result confirmed" },
  { value: "SETTLING", label: "Settling" },
  { value: "SETTLED", label: "Settled" },
  { value: "CANCELLED", label: "Cancelled" },
];

export const FINAL_STATUSES = ["SETTLED", "CANCELLED"];
export const RESOLUTION_STATUSES = ["CLOSED", "AWAITING_RESULT", "RESULT_PROPOSED", "RESULT_CONFIRMED", "SETTLING", "SETTLED"];
const PUBLISHABLE_STATUSES = ["SCHEDULED", "OPENING_AUCTION", "OPEN", "SUSPENDED", "CLOSED", "AWAITING_RESULT", "RESULT_PROPOSED", "RESULT_CONFIRMED", "SETTLING", "SETTLED"];

export function statusLabel(status) {
  return STATUS_LABEL[status] || status;
}

export function statusTone(status) {
  return STATUS_TONE[status] || "gray";
}

export function isDecisionDecidable(decision, nowMs) {
  if (!decision) return false;
  if (decision.disputedAt) return true;
  if (!decision.disputeWindowEndsAt) return true;
  return new Date(decision.disputeWindowEndsAt).getTime() <= nowMs;
}

export function canFileDispute(decision, nowMs) {
  if (!decision || decision.status !== "PENDING" || decision.disputedAt) return false;
  if (!decision.disputeWindowEndsAt) return false;
  return new Date(decision.disputeWindowEndsAt).getTime() > nowMs;
}

export function formatCountdown(ms) {
  if (ms <= 0) return "0m";
  const totalMinutes = Math.ceil(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

const PARTNER_ACTIONS = [
  {
    key: "submit-for-approval",
    label: "Submit for approval",
    button: "info",
    title: "Submit this market for approval?",
    description: "It goes to your tenant for review. You can still cancel it while it waits.",
    when: (m) => m.status === "DRAFT",
  },
  {
    key: "suspend",
    label: "Suspend",
    button: "warning",
    title: "Suspend trading on this market?",
    description: "Nobody can buy or sell until it is resumed. Open positions are untouched.",
    when: (m) => m.status === "OPEN",
  },
  {
    key: "resume",
    label: "Resume",
    button: "success",
    title: "Resume trading on this market?",
    when: (m) => m.status === "SUSPENDED",
  },
  {
    key: "cancel",
    label: "Cancel market",
    button: "danger",
    money: true,
    title: "Cancel this market?",
    description: "This moves money: every open order is cancelled and every trader is refunded in full. It cannot be undone.",
    when: (m) => !FINAL_STATUSES.includes(m.status),
  },
];

const TENANT_ACTIONS = [
  {
    key: "submit-for-approval",
    label: "Publish",
    permission: "market.update",
    button: "info",
    title: "Publish this market?",
    description: "It goes on the board and opens on its schedule. No second approval is needed.",
    when: (m) => m.status === "DRAFT",
  },
  {
    key: "approve",
    label: "Approve",
    permission: "market.approve",
    button: "success",
    selfBlock: "creator",
    title: "Approve this market?",
    description: "It is scheduled and opens for trading at its open time. Publish it to put it on the storefront.",
    when: (m) => m.status === "PENDING_APPROVAL",
  },
  {
    key: "reject",
    label: "Reject",
    permission: "market.approve",
    button: "danger",
    title: "Reject and send it back to draft?",
    description: "Its creator sees your reason and can fix it and resubmit.",
    when: (m) => m.status === "PENDING_APPROVAL",
  },
  {
    key: "publish",
    label: "Publish",
    permission: "market.approve",
    button: "primary",
    selfBlock: "creator",
    needs: ["publishedAt", "approvedAt"],
    title: "Publish this market to the storefront?",
    when: (m) => !m.publishedAt && PUBLISHABLE_STATUSES.includes(m.status),
    blockedReason: (m) => {
      const reasons = [];
      if (!m.approvedAt) reasons.push("it has not been approved yet");
      if (m.visibility && m.visibility !== "PUBLIC") reasons.push(`its visibility is ${m.visibility}, not PUBLIC`);
      return reasons.length ? `Cannot publish yet: ${reasons.join("; ")}.` : null;
    },
  },
  {
    key: "unpublish",
    label: "Unpublish",
    permission: "market.approve",
    button: "outline",
    needs: ["publishedAt"],
    title: "Take this market off the storefront?",
    description: "Its status, trades and positions are unchanged. Only its public listing is removed.",
    when: (m) => Boolean(m.publishedAt),
  },
  {
    key: "begin-auction",
    label: "Begin opening auction",
    permission: "market.update",
    button: "info",
    title: "Begin the opening auction?",
    when: (m) => m.status === "SCHEDULED",
  },
  {
    key: "open",
    label: "Open for trading",
    permission: "market.update",
    button: "success",
    title: "Open this market for continuous trading?",
    when: (m) => m.status === "OPENING_AUCTION",
  },
  {
    key: "suspend",
    label: "Suspend",
    permission: "market.suspend",
    button: "warning",
    title: "Suspend trading on this market?",
    description: "Nobody can buy or sell until it is resumed. Open positions are untouched.",
    when: (m) => m.status === "OPEN",
  },
  {
    key: "resume",
    label: "Resume",
    permission: "market.suspend",
    button: "success",
    title: "Resume trading on this market?",
    when: (m) => m.status === "SUSPENDED",
  },
  {
    key: "close",
    label: "Close trading",
    permission: "market.update",
    button: "neutral",
    title: "Close this market to further trading?",
    description: "It then waits for its result. You can reopen it only while its scheduled close time is still ahead.",
    when: (m) => m.status === "OPEN" || m.status === "SUSPENDED",
  },
  {
    key: "reopen",
    label: "Reopen",
    permission: "market.suspend",
    button: "outline",
    needs: ["closesAt"],
    title: "Reopen this market for trading?",
    description: "Only for a market closed by mistake ahead of its own close time, and only before any result is proposed.",
    when: (m) => (m.status === "CLOSED" || m.status === "AWAITING_RESULT") && new Date(m.closesAt).getTime() > Date.now(),
  },
  {
    key: "begin-result-collection",
    label: "Begin result collection",
    permission: "market.update",
    button: "info",
    title: "Begin collecting the result?",
    when: (m) => m.status === "CLOSED",
  },
  {
    key: "propose-result",
    label: "Propose result",
    permission: "market.settle",
    button: "primary",
    form: "propose",
    needs: ["outcomes"],
    when: (m) => m.status === "AWAITING_RESULT",
  },
  {
    key: "approve-result",
    label: "Confirm result",
    permission: "market.settle",
    button: "success",
    selfBlock: "proposer",
    decisionGate: true,
    title: "Confirm the proposed result?",
    description: "Once confirmed, the market can be settled and correct traders paid.",
    when: (m) => m.status === "RESULT_PROPOSED",
  },
  {
    key: "reject-result",
    label: "Reject result",
    permission: "market.settle",
    button: "danger",
    selfBlock: "proposer",
    decisionGate: true,
    title: "Reject the proposed result?",
    description: "The market goes back to awaiting result so a correct one can be proposed.",
    when: (m) => m.status === "RESULT_PROPOSED",
  },
  {
    key: "dispute-result",
    label: "Dispute result",
    permission: "market.settle",
    button: "warning",
    needs: ["decision"],
    title: "Dispute this proposed result?",
    description: "This flags the proposal for review. It does not change the market's status by itself.",
    when: (m, ctx) => m.status === "RESULT_PROPOSED" && canFileDispute(ctx.decision, Date.now()),
  },
  {
    key: "settle",
    label: "Settle and pay out",
    permission: "market.settle",
    button: "primary",
    money: true,
    title: "Settle this market and pay out?",
    description: "This moves money: correct traders split the pool and commission is booked. It cannot be undone.",
    when: (m) => m.status === "RESULT_CONFIRMED",
  },
  {
    key: "settle-positions",
    label: "Retry payouts",
    permission: "market.settle",
    button: "outline",
    money: true,
    noReason: true,
    needs: ["full"],
    title: "Run payouts again for any unpaid positions on the correct outcome?",
    description: "Settlement already pays correct traders. Use this only if some payouts did not complete. Positions already paid are skipped.",
    when: (m) => m.status === "SETTLED",
  },
  {
    key: "cancel",
    label: "Cancel market",
    permission: "market.cancel",
    button: "danger",
    money: true,
    title: "Cancel this market?",
    description: "This moves money: every open order is cancelled and every trader is refunded in full. It cannot be undone.",
    when: (m) => !FINAL_STATUSES.includes(m.status),
  },
  {
    key: "void-positions",
    label: "Retry refunds",
    permission: "market.cancel",
    button: "outline",
    money: true,
    noReason: true,
    needs: ["full"],
    title: "Run refunds again for any positions not yet refunded?",
    description: "Cancelling already refunds everyone. Use this only if some refunds did not complete.",
    when: (m) => m.status === "CANCELLED",
  },
];

function hasField(market, ctx, field) {
  if (field === "decision") return ctx.decision !== undefined && ctx.decision !== null;
  if (field === "full") return Boolean(ctx.full);
  return market[field] !== undefined;
}

export function availableActions(market, audience, ctx = {}) {
  if (!market || !audience || !audience.canManage) return [];
  const userId = audience.userId;
  if (audience.isPartner) {
    return PARTNER_ACTIONS.filter((a) => a.when(market, ctx)).map((a) => ({ ...a, via: "portal", disabledReason: null }));
  }
  if (!audience.isTenant || !audience.tenantId) return [];
  return TENANT_ACTIONS.filter((a) => audience.can(a.permission))
    .filter((a) => (a.needs || []).every((field) => hasField(market, ctx, field)))
    .filter((a) => a.when(market, ctx))
    .map((a) => {
      let disabledReason = a.blockedReason ? a.blockedReason(market) : null;
      if (!disabledReason && a.selfBlock === "creator" && userId && market.createdBy === userId) {
        disabledReason = "Two-person rule: you created this market, so a teammate has to do this.";
      }
      if (!disabledReason && a.selfBlock === "proposer" && userId && ctx.decision && ctx.decision.proposedBy === userId) {
        disabledReason = "Two-person rule: you proposed this result, so a teammate has to confirm or reject it.";
      }
      if (!disabledReason && a.decisionGate && ctx.decision && !isDecisionDecidable(ctx.decision, Date.now())) {
        disabledReason = "The dispute window is still open. Wait for it to close, or file a dispute first.";
      }
      return { ...a, via: "admin", disabledReason };
    });
}

export function actionUrl(action, market, audience) {
  if (action.via === "portal") return `/portal/markets/${market.id}/${action.key}`;
  return `/admin/tenants/${audience.tenantId}/markets/${market.id}/${action.key}`;
}

export function describeActionError(err) {
  const message = err?.message || "Something went wrong.";
  switch (err?.code) {
    case "SEPARATION_OF_DUTIES":
    case "DUPLICATE_RESULT_APPROVER":
      return `Two-person rule: ${message} Ask a teammate to do this step.`;
    case "INVALID_MARKET_STATUS_TRANSITION":
      return `This market's status has changed since the page loaded, so that step no longer applies (${message}). Refresh to see where it is now.`;
    case "FORBIDDEN":
      return "Your account is not allowed to do this. Ask your administrator for access.";
    case "DISPUTE_WINDOW_STILL_OPEN":
      return `The dispute window is still open: ${message}`;
    default:
      return message;
  }
}
